// Pure helpers for the shell (047 D10, D11): the active menu item, the breadcrumb, the top
// bar's dates, the search shortcut's label. No React and no MUI: Jest loads this file.
import { MONTHS, parts } from "./queuePresentation";

// The menu path a page belongs to (047 D10): "/" lights only itself; any other page lights
// the longest menu path it equals or sits under, so /students/abc/wallet lights Students.
// "" when none does.
export const activeMenuPath = (pathname, paths = []) => {
  const path = String(pathname || "");
  return paths
    .filter((p) => (p === "/" ? path === "/" : path === p || path.startsWith(`${p}/`)))
    .reduce((best, p) => (p.length > best.length ? p : best), "");
};

// The top bar's trail (047 D11). `items`: the menu items the role sees ({ text, section,
// path, Icon }). `sections`: NAV_SECTIONS. `names`: the names pages handed up, by id.
// `icons`: { home, person, sections: { [key]: Icon } }. A crumb is { label, to, Icon }; the
// last one is the page itself, so the top bar gives it no link.
export const breadcrumbTrail = (pathname, { items = [], sections = [], names = {}, icons = {} } = {}) => {
  const path = String(pathname || "");
  const person = path.match(/^\/students\/([^/]+)(?:\/children\/([^/]+))?/);
  if (person) {
    const [, id, childId] = person;
    const students = { label: "Students", to: "/students", Icon: items.find((i) => i.path === "/students")?.Icon };
    return childId
      ? [
          students,
          { label: names[id] || "…", to: `/students/${id}`, Icon: null },
          { label: names[childId] || "…", to: null, Icon: icons.person },
        ]
      : [students, { label: names[id] || "…", to: null, Icon: icons.person }];
  }
  // The tutor page and its tabs (047 step 4): Tutors / name.
  const tutor = path.match(/^\/tutors\/([^/]+)/);
  if (tutor) {
    const tutors = { label: "Tutors", to: "/tutors", Icon: items.find((i) => i.path === "/tutors")?.Icon };
    return [tutors, { label: names[tutor[1]] || "…", to: null, Icon: icons.person }];
  }
  // The ticket page (047 step 5; Figma's 10830:94489) and the lesson page (047 step 6): Support /
  // Inbox / #12 and Support / Lessons / #a1B2c3D4. The number has no icon: the person icon marks
  // a person's name, as on the student and tutor pages.
  const record = path.match(/^\/(inbox|lessons)\/([^/]+)/);
  if (record) {
    const [, list, id] = record;
    const item = items.find((i) => i.path === `/${list}`);
    const group = sections.find((s) => s.key === item?.section);
    return [
      ...(group ? [{ label: group.caption, to: null, Icon: icons.sections?.[group.key] }] : []),
      { label: item?.text || (list === "inbox" ? "Inbox" : "Lessons"), to: `/${list}`, Icon: null },
      { label: names[id] || "…", to: null, Icon: null },
    ];
  }
  // The New notification page and an announcement's page (044 D47): Growth / Notifications /
  // "New notification" or the announcement's title, built like the ticket page's trail.
  const announcement = path.match(/^\/notifications\/([^/]+)/);
  if (announcement) {
    const list = items.find((i) => i.path === "/notifications");
    const group = sections.find((s) => s.key === list?.section);
    const id = announcement[1];
    return [
      ...(group ? [{ label: group.caption, to: null, Icon: icons.sections?.[group.key] }] : []),
      { label: list?.text || "Notifications", to: "/notifications", Icon: null },
      { label: id === "new" ? "New notification" : names[id] || "…", to: null, Icon: null },
    ];
  }
  const active = activeMenuPath(path, items.map((i) => i.path));
  const item = items.find((i) => i.path === active);
  if (!item) return [{ label: "Home", to: null, Icon: icons.home }];
  const section = item.section === "home" ? null : sections.find((s) => s.key === item.section);
  return [
    { label: "Home", to: "/", Icon: icons.home },
    ...(section ? [{ label: section.caption, to: null, Icon: icons.sections?.[section.key] }] : []),
    { label: item.text, to: null, Icon: item.Icon },
  ];
};

// "14 Sep 2026" over "3 Rabiʻ II 1448 AH" (047 D11). The Hijri date is Umm al-Qura, which can
// be a day off local moon sighting. The reader's zone unless one is given (tests pin UTC).
export const dateLines = (date, { timeZone } = {}) => {
  const p = parts(date, { day: "numeric", month: "numeric", year: "numeric", timeZone });
  const hijri = new Intl.DateTimeFormat("en-GB-u-ca-islamic-umalqura", {
    day: "numeric",
    month: "long",
    year: "numeric",
    timeZone,
  }).format(date);
  return { gregorian: `${Number(p.day)} ${MONTHS[Number(p.month) - 1]} ${p.year}`, hijri };
};

// The search box's key hint (047 D11): "⌘ K" on Apple devices, "Ctrl K" elsewhere.
export const shortcutLabel = (platform) =>
  /mac|iphone|ipad|ipod/i.test(String(platform || "")) ? "⌘ K" : "Ctrl K";
